import React, { useState } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import Table from '../components/common/Table';
import Modal from '../components/common/Modal';
import Input from '../components/common/Input';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { CheckCircle, XCircle, Download, Search } from 'lucide-react';
import { formatDate, formatCurrency, formatDateTime, delay } from '../utils/helpers';
import { generateJustificanteAutorizacionPDF } from '../services/pdfService';
import toast from 'react-hot-toast';

const AveriasListPage = () => {
  const { mockData } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('todas');
  const [searchTerm, setSearchTerm] = useState('');

  const averias = React.useMemo(() => {
    let filtered = mockData?.averias || [];

    if (activeTab !== 'todas') {
      const tabFilters = {
        'en_curso': ['pendiente', 'en_curso'],
        'pendientes': ['pendiente'],
        'autorizadas': ['autorizada'],
        'finalizadas': ['finalizada', 'facturada'],
        'rechazadas': ['rechazada']
      };
      filtered = filtered.filter(a => tabFilters[activeTab]?.includes(a.estado));
    }

    if (searchTerm) {
      filtered = filtered.filter(a =>
        a.numeroAveria.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.tipo.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [mockData, activeTab, searchTerm]);

  const tabs = [
    { id: 'todas', label: 'Todas', count: mockData?.averias.length || 0 },
    { id: 'en_curso', label: 'En Curso', count: mockData?.averias.filter(a => ['pendiente', 'en_curso'].includes(a.estado)).length || 0 },
    { id: 'pendientes', label: 'Pendientes', count: mockData?.averias.filter(a => a.estado === 'pendiente').length || 0 },
    { id: 'autorizadas', label: 'Autorizadas', count: mockData?.averias.filter(a => a.estado === 'autorizada').length || 0 },
    { id: 'finalizadas', label: 'Finalizadas', count: mockData?.averias.filter(a => ['finalizada', 'facturada'].includes(a.estado)).length || 0 }
  ];

  const getEstadoBadge = (estado) => {
    const variants = {
      'pendiente': 'warning',
      'en_curso': 'info',
      'autorizada': 'success',
      'rechazada': 'danger',
      'finalizada': 'default',
      'facturada': 'primary'
    };
    return <Badge variant={variants[estado]}>{estado.toUpperCase().replace('_', ' ')}</Badge>;
  };

  const columns = [
    {
      header: 'Nº Avería',
      cell: (row) => (
        <span className="font-mono text-sm font-semibold">{row.numeroAveria}</span>
      )
    },
    {
      header: 'Tipo',
      accessor: 'tipo'
    },
    {
      header: 'Fecha Reporte',
      cell: (row) => formatDate(row.fechaReporte)
    },
    {
      header: 'Estado',
      cell: (row) => getEstadoBadge(row.estado)
    },
    {
      header: 'Cobertura',
      cell: (row) => (
        <Badge variant={row.cubierta ? 'success' : 'danger'}>
          {row.cubierta ? 'CUBIERTA' : 'NO CUBIERTA'}
        </Badge>
      )
    },
    {
      header: 'Coste',
      cell: (row) => (
        <span className="font-semibold">{formatCurrency(row.costeEstimado)}</span>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Averías</h1>
        <p className="text-gray-600 mt-1">Gestión y autorización de averías</p>
      </div>

      <Card>
        <div className="border-b border-gray-200 mb-6">
          <div className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100">
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <Input
            placeholder="Buscar por número de avería o tipo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
        </div>

        <Table
          columns={columns}
          data={averias}
          onRowClick={(row) => navigate(`/averias/${row.id}`)}
        />
      </Card>
    </div>
  );
};

const AveriaDetallePage = () => {
  const { id } = useParams();
  const { mockData, updateMockData, addAuditLog, user } = useAuth();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();

  const [showAutorizarModal, setShowAutorizarModal] = useState(false);
  const [showRechazarModal, setShowRechazarModal] = useState(false);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [loading, setLoading] = useState(false);

  const averia = mockData?.averias.find(a => a.id === id);
  const garantia = mockData?.garantias.find(g => g.id === averia?.garantiaId);

  if (!averia || !garantia) {
    return (
      <div className="space-y-6">
        <Card>
          <p className="text-center text-gray-500">Avería no encontrada</p>
        </Card>
      </div>
    );
  }

  const handleAutorizar = async () => {
    if (!aceptaTerminos) {
      toast.error('Debe aceptar los términos para continuar');
      return;
    }

    setLoading(true);
    await delay(1500);

    const evidenciaLegal = {
      timestamp: new Date().toISOString(),
      ip: '192.168.1.' + Math.floor(Math.random() * 255),
      userAgent: navigator.userAgent,
      hash: Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join('')
    };

    const averiaActualizada = {
      ...averia,
      estado: 'autorizada',
      fechaAutorizacion: new Date().toISOString(),
      autorizadoPor: user.email,
      evidenciaLegal
    };

    const updatedAverias = mockData.averias.map(a =>
      a.id === id ? averiaActualizada : a
    );

    updateMockData({ averias: updatedAverias });
    addAuditLog('autorizar_averia', 'averia', id, `Avería ${averia.numeroAveria} autorizada`);
    addNotification('success', 'Avería Autorizada', `La avería ${averia.numeroAveria} ha sido autorizada`);

    toast.success('Avería autorizada correctamente');
    setLoading(false);
    setShowAutorizarModal(false);

    generateJustificanteAutorizacionPDF(averiaActualizada, garantia);

    setTimeout(() => {
      navigate('/averias');
    }, 1000);
  };

  const handleRechazar = async () => {
    setLoading(true);
    await delay(1000);

    const averiaActualizada = {
      ...averia,
      estado: 'rechazada'
    };

    const updatedAverias = mockData.averias.map(a =>
      a.id === id ? averiaActualizada : a
    );

    updateMockData({ averias: updatedAverias });
    addAuditLog('rechazar_averia', 'averia', id, `Avería ${averia.numeroAveria} rechazada`);
    addNotification('warning', 'Avería Rechazada', `La avería ${averia.numeroAveria} ha sido rechazada`);

    toast.success('Avería rechazada');
    setLoading(false);
    setShowRechazarModal(false);

    setTimeout(() => {
      navigate('/averias');
    }, 1000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Detalle de Avería</h1>
          <p className="text-gray-600 mt-1">{averia.numeroAveria}</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/averias')}>
          Volver
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Información de la Avería">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Número de Avería</p>
                <p className="font-semibold">{averia.numeroAveria}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Tipo</p>
                <p className="font-semibold">{averia.tipo}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Fecha Reporte</p>
                <p className="font-semibold">{formatDate(averia.fechaReporte)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Estado</p>
                <div className="mt-1">
                  <Badge variant={averia.estado === 'autorizada' ? 'success' : averia.estado === 'rechazada' ? 'danger' : 'warning'}>
                    {averia.estado.toUpperCase()}
                  </Badge>
                </div>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Descripción</p>
                <p className="font-semibold">{averia.descripcion}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Taller</p>
                <p className="font-semibold">{averia.taller}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Coste Estimado</p>
                <p className="font-semibold text-lg">{formatCurrency(averia.costeEstimado)}</p>
              </div>
            </div>
          </Card>

          <Card title="Evaluación de Cobertura">
            <div className="flex items-start space-x-4">
              {averia.cubierta ? (
                <>
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-success-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-success-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-success-700 mb-2">AVERÍA CUBIERTA</h3>
                    <p className="text-gray-600">
                      Esta avería está cubierta por la garantía del vehículo según el nivel de cobertura contratado.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-danger-100 rounded-full flex items-center justify-center">
                      <XCircle className="w-6 h-6 text-danger-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-danger-700 mb-2">AVERÍA NO CUBIERTA</h3>
                    <p className="text-gray-600 mb-2">
                      Esta avería no está cubierta por la garantía.
                    </p>
                    <p className="text-sm text-gray-500">
                      <strong>Motivo:</strong> {averia.motivoRechazo}
                    </p>
                  </div>
                </>
              )}
            </div>
          </Card>

          <Card title="Vehículo Afectado">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Matrícula</p>
                <p className="font-semibold">{garantia.vehiculo.matricula}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Marca/Modelo</p>
                <p className="font-semibold">{garantia.vehiculo.marca} {garantia.vehiculo.modelo}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Propietario</p>
                <p className="font-semibold">{garantia.propietario.nombre}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Nº Garantía</p>
                <p className="font-semibold">{garantia.numeroGarantia}</p>
              </div>
            </div>
          </Card>

          {averia.evidenciaLegal && (
            <Card title="Evidencia Legal de Autorización">
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Timestamp</p>
                  <p className="font-mono text-sm">{formatDateTime(averia.evidenciaLegal.timestamp)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Autorizado por</p>
                  <p className="font-semibold">{averia.autorizadoPor}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">IP</p>
                  <p className="font-mono text-sm">{averia.evidenciaLegal.ip}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Hash de Verificación</p>
                  <p className="font-mono text-xs break-all">{averia.evidenciaLegal.hash}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  icon={Download}
                  onClick={() => generateJustificanteAutorizacionPDF(averia, garantia)}
                >
                  Descargar Justificante
                </Button>
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          {averia.estado === 'pendiente' && averia.cubierta && (
            <Card title="Acciones">
              <div className="space-y-3">
                <Button
                  variant="success"
                  fullWidth
                  icon={CheckCircle}
                  onClick={() => setShowAutorizarModal(true)}
                >
                  AUTORIZAR REPARACIÓN
                </Button>
                <Button
                  variant="danger"
                  fullWidth
                  icon={XCircle}
                  onClick={() => setShowRechazarModal(true)}
                >
                  RECHAZAR
                </Button>
              </div>
            </Card>
          )}



          <Card title="Timeline">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary-600 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-semibold">Avería reportada</p>
                  <p className="text-xs text-gray-500">{formatDateTime(averia.fechaReporte)}</p>
                </div>
              </div>
              {averia.fechaAutorizacion && (
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-success-600 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-semibold">Avería autorizada</p>
                    <p className="text-xs text-gray-500">{formatDateTime(averia.fechaAutorizacion)}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      <Modal
        isOpen={showAutorizarModal}
        onClose={() => setShowAutorizarModal(false)}
        title="Autorizar Reparación"
        footer={
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowAutorizarModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="success"
              onClick={handleAutorizar}
              loading={loading}
              disabled={!aceptaTerminos}
            >
              Confirmar Autorización
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <p className="text-sm text-warning-800">
              <strong>Importante:</strong> Al autorizar esta reparación, está confirmando que la avería está cubierta
              por la garantía y que se procederá con la reparación. Esta acción quedará registrada legalmente.
            </p>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Número de Avería:</span>
              <span className="font-semibold">{averia.numeroAveria}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Coste Estimado:</span>
              <span className="font-semibold">{formatCurrency(averia.costeEstimado)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Taller:</span>
              <span className="font-semibold">{averia.taller}</span>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={aceptaTerminos}
                onChange={(e) => setAceptaTerminos(e.target.checked)}
                className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                He leído y acepto los términos de autorización. Confirmo que esta avería está cubierta
                por la garantía y autorizo su reparación. Entiendo que esta acción quedará registrada
                con evidencia legal (timestamp, IP, hash de verificación).
              </span>
            </label>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showRechazarModal}
        onClose={() => setShowRechazarModal(false)}
        title="Rechazar Avería"
        footer={
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowRechazarModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleRechazar}
              loading={loading}
            >
              Confirmar Rechazo
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            ¿Está seguro de que desea rechazar esta avería? Esta acción no se puede deshacer.
          </p>
        </div>
      </Modal>
    </div>
  );
};

const AveriasPage = () => {
  return (
    <Layout>
      <Routes>
        <Route index element={<AveriasListPage />} />
        <Route path=":id" element={<AveriaDetallePage />} />
      </Routes>
    </Layout>
  );
};

export default AveriasPage;
